import { useEffect, useState, type RefObject } from 'react'

export interface UseProximityOptions {
  /** Distance in px from the element's bounding box at which `near` flips true. Default `96`. */
  radius?: number
  /**
   * When `false`, no `mousemove` listener is registered and `near` stays
   * `false`. Gate this with an `isActive` tab flag: an inactive TabStack tab is
   * mounted with `display:none`, whose rect is all-zero and would falsely report
   * near for cursors near the viewport origin — and every mounted instance would
   * otherwise keep a live `window` listener. Default `true`.
   */
  enabled?: boolean
  /**
   * Measure the element's PARENT box instead of the element itself. For
   * affordances whose interactive row is much smaller than the zone it
   * belongs to (e.g. the compact AddTask row inside its full-width
   * placeholder band): the proximity then reads "how close is the cursor to
   * this LINE", independent of the horizontal position. Default `false`.
   */
  measureParent?: boolean
}

/**
 * Tracks whether the pointer is within `radius` px of the referenced element's
 * bounding box — the "Near" proximity affordance (an element reacts as the
 * cursor approaches, *before* `:hover`).
 *
 * The `mousemove` handler is rAF-throttled (at most one `getBoundingClientRect`
 * + state check per frame), so many simultaneous instances — e.g. one per task-
 * list section — stay cheap.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null)
 * const near = useProximity(ref, { enabled: isActiveTab })
 * // <div ref={ref} className={cn('thing', { 'thing--near': near })} />
 */
export function useProximity<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { radius = 96, enabled = true }: UseProximityOptions = {},
): boolean {
  const [near, setNear] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setNear(false)
      return
    }
    const el = ref.current
    if (!el) return

    let frame = 0
    const handleMove = (e: MouseEvent) => {
      // Coalesce bursts of mousemove events to one measurement per frame.
      if (frame) return
      const { clientX, clientY } = e
      frame = requestAnimationFrame(() => {
        frame = 0
        const r = el.getBoundingClientRect()
        const dx = Math.max(r.left - clientX, 0, clientX - r.right)
        const dy = Math.max(r.top - clientY, 0, clientY - r.bottom)
        setNear(Math.hypot(dx, dy) <= radius)
      })
    }

    window.addEventListener('mousemove', handleMove)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('mousemove', handleMove)
    }
  }, [ref, radius, enabled])

  return near
}

// ── Nearest-only variant ──────────────────────────────────────────────────────
// All enabled instances share ONE registry + mousemove loop; per frame, only
// the instance closest to the cursor (within its radius) reports `near`. Use
// where several identical affordances would otherwise light up together —
// e.g. the compact list's AddTask plus per section.

interface NearestEntry {
  el: HTMLElement
  radius: number
  update: (near: boolean) => void
}

const nearestEntries = new Set<NearestEntry>()
let nearestFrame = 0
let nearestListener: ((e: MouseEvent) => void) | null = null

function ensureNearestListener() {
  if (nearestListener) return
  nearestListener = (e: MouseEvent) => {
    if (nearestFrame) return
    const { clientX, clientY } = e
    nearestFrame = requestAnimationFrame(() => {
      nearestFrame = 0
      let best: NearestEntry | null = null
      let bestDist = Infinity
      for (const entry of nearestEntries) {
        const r = entry.el.getBoundingClientRect()
        // Hidden (display:none TabStack slot) → all-zero rect near the viewport
        // origin; never a candidate.
        if (r.width === 0 && r.height === 0) continue
        const dx = Math.max(r.left - clientX, 0, clientX - r.right)
        const dy = Math.max(r.top - clientY, 0, clientY - r.bottom)
        const dist = Math.hypot(dx, dy)
        if (dist <= entry.radius && dist < bestDist) {
          bestDist = dist
          best = entry
        }
      }
      for (const entry of nearestEntries) entry.update(entry === best)
    })
  }
  window.addEventListener('mousemove', nearestListener)
}

function teardownNearestListenerIfIdle() {
  if (!nearestListener || nearestEntries.size > 0) return
  window.removeEventListener('mousemove', nearestListener)
  nearestListener = null
  if (nearestFrame) {
    cancelAnimationFrame(nearestFrame)
    nearestFrame = 0
  }
}

/**
 * Like {@link useProximity}, but across ALL mounted instances only the one
 * nearest to the cursor (within `radius`) is `near` — the others stay off
 * even when the cursor is inside their radius too. Between two adjacent
 * zones the boundary is their midpoint (Voronoi-style): exactly one of them
 * lights up, whichever the cursor is closer to.
 */
export function useNearestProximity<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { radius = 96, enabled = true, measureParent = false }: UseProximityOptions = {},
): boolean {
  const [near, setNear] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setNear(false)
      return
    }
    const el = measureParent ? ref.current?.parentElement ?? ref.current : ref.current
    if (!el) return

    const entry: NearestEntry = {
      el,
      radius,
      update: (v) => setNear((prev) => (prev === v ? prev : v)),
    }
    nearestEntries.add(entry)
    ensureNearestListener()
    return () => {
      nearestEntries.delete(entry)
      teardownNearestListenerIfIdle()
    }
  }, [ref, radius, enabled, measureParent])

  return near
}
